// deps
import React from "react";

// internal deps
import { BillToCompanyGroup, RequestForBillImageParams } from "@swiggy-private/payment-ui";

// interfaces
import { PaymentGroupHandlerProps } from "@payment-client/components/show-payments/components/payment-group-handler/interfaces";

// hooks
import { usePaymentActions } from "@payment-client/components/show-payments/components/payment-group-handler/hooks/UsePaymentActions";
import { useExpenseCodeHalfCardContext } from "../../expense-code-provider-halfcard";

// helpers
import { isBillToCompanyGroup } from "@payment-client/helpers/BillToCompanyHelper";

// types
type BillToCompanyGroupHandlerProps = PaymentGroupHandlerProps & {
    requestForBillImage: (params: RequestForBillImageParams) => void;
    openBillImageWebview: (onBillImagesUpdated?: VoidFunction) => Promise<void>;
};

export function BillToCompanyGroupHandler(props: BillToCompanyGroupHandlerProps): React.ReactElement | null {
    const {
        onSelect,
        onPay,
        onPaymentWalletLoad,
        requestForBillImage,
        openBillImageWebview,
        paymentGroup,
        ...otherProps
    } = props;

    const {
        showExpenseCodeHalfCard,
        onOpenHalfcard,
        expenseCodeProps,
        isBillToCompanySelected,
        setOnSubmit,
        setIsFullPaymentMode,
    } = useExpenseCodeHalfCardContext();

    const paymentActions = {
        ...usePaymentActions({ onSelect, onPay, onPaymentWalletLoad }),
    };

    // Type guard to ensure this is a Bill to Company group
    if (!isBillToCompanyGroup(paymentGroup)) {
        return null;
    }

    // Create the proper IB2CPaymentGroup structure
    const b2cPaymentGroup = {
        paymentData: paymentGroup,
        paymentActions: {
            onSelect: paymentActions.onSelect,
            onPay: paymentActions.onPay,
            onPaymentWalletLoad: paymentActions.onPaymentWalletLoad,
            requestForBillImage,
        },
        openBillImageWebview,
        expenseCodeData: {
            showExpenseCodeHalfCard,
            expenseCodeDataInCache: expenseCodeProps.expenseCodeDataInCache,
            isBillToCompanySelected,
            onOpenHalfcard,
            setOnSubmit,
            onSubmit: expenseCodeProps.onSubmit,
            setIsFullPaymentMode,
        },
        ...otherProps,
    };

    return <BillToCompanyGroup {...b2cPaymentGroup} />;
}
